import React, { useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import './ScienceDropdown.css';
import Loading from '../utilities/Loading';
import { Col, Container, Row } from 'react-bootstrap';
import FormElementHeader from './FormElementHeader';
import { useTranslation } from 'react-i18next';
import HelpDialog from '../dialog/HelpDialog';
import DropDown from './DropDown';
import RecordTag from '../utilities/RecordTag';

const fetchSciences = async () => {
  const URL = `${process.env.REACT_APP_LATAAMO_PROXY_SERVER}/api/sciences`;
  try {
    const response = await fetch(URL);
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Unexpected status code from ${URL}`, {
      cause: { status: response.status }
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const ScienceDropdown = (props) => {
  const { selected, onChange, disabled, message, inheritedScience, inheritedScienceLoading = false, placeholder, className = '', required = true } = props;
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [sciences, setSciences] = useState(null);

  const id = useId();

  useEffect(() => {
    if (!sciences) {
      (async () => {
        setSciences(await fetchSciences());
        setLoading(false);
      })();
    }
  }, [loading, sciences]);

  const language = i18n.language;
  const orderedSciences = (sciences || []).filter(science => String(science.id) !== '-1');
  const sciencesById = new Map(orderedSciences.map(science => [String(science.id), science]));
  const inheritedScienceData = sciencesById.get(String(inheritedScience));
  const inheritedScienceName = inheritedScienceData?.names?.[language] || inheritedScienceData?.names?.en || inheritedScienceData?.names?.fi;
  const inheritedScienceLabel = inheritedScienceName?.replace(/^\s*\d+\s+/, '') || t('science_not_selected');
  const showInheritedScience = Object.hasOwn(props, 'inheritedScience');
  const getDepth = science => {
    let depth = 0;
    let parentId = science.parent;
    const visited = new Set([String(science.id)]);

    while (parentId !== undefined && parentId !== null && !visited.has(String(parentId))) {
      visited.add(String(parentId));
      depth += 1;
      parentId = sciencesById.get(String(parentId))?.parent;
    }

    return depth;
  };

  const options = [
    { label: placeholder || t('science_option_default_select'), value: '' },
    { label: t('science_option_no_science'), value: -1 },
    ...orderedSciences.map(science => {
      const name = science.names?.[language] || science.names?.en || science.names?.fi || '';
      const label = name.replace(/^\s*\d+\s+/, '');
      const depth = getDepth(science);
      const isSelected = String(science.id) === String(selected);

      return {
        label: `${'　'.repeat(depth)}${depth && !isSelected ? '↳ ' : ''}${label}`,
        value: science.id
      };
    })
  ];

  return (
    <Loading loading={loading}>
      <Container className={`science-dropdown-container ${className}`.trim()}>
        <Row>
          <Col>
            <FormElementHeader
                id={id}
                helpDialog={
                  <HelpDialog label={t('science_help_label')}>
                    {t('science_help_content')}
                  </HelpDialog>
                }
                required={required}
            >
              {t('science_header')}
            </FormElementHeader>
          </Col>
        </Row>
        {showInheritedScience && (
          <Row className="science-after-header">
            <Col>
              <div className="science-inheritance-message">
                {t('record_inherited_science_label')}
                {inheritedScienceLoading ? (
                  <span className="science-inheritance-loading" role="status">{t('loading')}</span>
                ) : (
                  <RecordTag label={inheritedScienceLabel} color="black" />
                )}
              </div>
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <DropDown
              aria-labelledby={id}
              options={options}
              message={message}
              value={selected}
              aria-required={required}
              disabled={disabled}
              onChange={e => onChange(e.target.value)} />
          </Col>
        </Row>
      </Container>
    </Loading>
  );

};

ScienceDropdown.propTypes = {
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  inheritedScience: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inheritedScienceLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  message: PropTypes.shape({
    content: PropTypes.string,
    type: PropTypes.oneOf(['light', 'neutral', 'warning'])
  })
};

export default ScienceDropdown;
